import Hls from 'hls.js';
import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import VideoProp from '../propTypes/videoProp';
import { isHlsSource, resolveVideoUrl } from '../../services/utilityFunctions';

export default function VideoPlayer({
  video,
  onVideoFinished,
  style,
  startPlaying,
  forceHls,
  contentType,
}) {
  const videoRef = useRef(null);
  // BRAVON-6332 Phase 2b — a privatized source (`User.video`, a chat video, a
  // session replay) is stored as the authenticated `/file/<type>/<id>` route,
  // which a `<video src>` cannot send a Bearer token to. Re-based onto this
  // portal's own session-gated `/file/*`; a legacy public url is untouched.
  const url = resolveVideoUrl(video?.url);

  useEffect(() => {
    if (!url || !videoRef.current) return;

    let hls;

    // NOT `url.endsWith('.m3u8')`: a tokened/signed playlist carries a query
    // string, which used to drop HLS onto the mp4 branch and paint black.
    const isHls = forceHls || isHlsSource(url, contentType);

    if (isHls && Hls.isSupported()) {
      hls = new Hls({
        enableWorker: true,
        lowLatencyMode: false,
        backBufferLength: 90,
        capLevelToPlayerSize: true,
      });

      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        if (startPlaying) {
          videoRef.current.play().catch(err => {
            console.error('Play failed:', err);
          });
        }
      });

      hls.on(Hls.Events.ERROR, (event, data) => {
        if (data.fatal) {
          switch (data.type) {
            case Hls.ErrorTypes.NETWORK_ERROR:
              hls.startLoad();
              break;
            case Hls.ErrorTypes.MEDIA_ERROR:
              hls.recoverMediaError();
              break;
            default:
              hls.destroy();
              break;
          }
        }
      });

      hls.loadSource(url);
      hls.attachMedia(videoRef.current);
    } else if (videoRef.current.canPlayType('application/vnd.apple.mpegurl')) {
      // Safari native HLS
      videoRef.current.src = url;
      if (startPlaying) {
        videoRef.current.play().catch(err => {
          console.error('Play failed:', err);
        });
      }
    } else {
      // Normal MP4 fallback
      videoRef.current.src = url;
    }

    return () => {
      if (hls) {
        hls.destroy();
      }
    };
  }, [url, startPlaying, forceHls, contentType]);

  const containerStyle = {
    ...style,
    width: '100%',
    height: '100%',
    outline: 'none',
  };

  return (
    <video
      ref={videoRef}
      controls
      muted
      playsInline
      style={containerStyle}
      crossOrigin="anonymous"
      preload="metadata"
      onEnded={onVideoFinished}
      autoPlay={startPlaying}
      controlsList="nodownload"
    />
  );
}

VideoPlayer.propTypes = {
  video: PropTypes.shape(VideoProp.propType).isRequired,
  onVideoFinished: PropTypes.func,
  style: PropTypes.shape({ width: PropTypes.number, height: PropTypes.number }),
  startPlaying: PropTypes.bool,
  forceHls: PropTypes.bool,
  /** Media type reported by the `/link` resolver, when the caller has one. */
  contentType: PropTypes.string,
};

VideoPlayer.defaultProps = {
  onVideoFinished: () => null,
  style: {},
  startPlaying: false,
  forceHls: false,
  contentType: null,
};
